import React from 'react';
import { Box } from '@mui/material';
import { colors, spacing } from '../../constants';
import { useRandomQuote } from '../../hooks/shared/useRandomQuote';
import {
  BACKGROUND_IMAGE_URL,
  METRICS_DATA,
  SERVICES_DATA,
  CHART_BAR_HEIGHTS,
  HEADING_DATA,
} from '../../constants/promotionalSection';
import PromotionalHeading from './promotionalSection/PromotionalHeading';
import MetricCard from './promotionalSection/MetricCard';
import AnalyticsChart from './promotionalSection/AnalyticsChart';
import TopServicesList from './promotionalSection/TopServicesList';
import TestimonialCard from './promotionalSection/TestimonialCard';

/**
 * PromotionalSection Component
 * Promotional section displayed on the login/signup page
 */
const PromotionalSection = () => {
  const randomQuote = useRandomQuote();

  return (
    <Box
      sx={{
        width: { xs: '0%', md: '40%' },
        display: { xs: 'none', md: 'flex' },
        alignItems: 'center',
        justifyContent: 'center',
        position: 'relative',
        backgroundColor: '#F0F2F5',
        overflow: 'hidden',
        height: '100vh',
        backgroundImage: `url(${BACKGROUND_IMAGE_URL})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'linear-gradient(to bottom, rgba(0, 0, 0, 0.5), rgba(0, 0, 0, 0.30))',
          zIndex: 1,
        },
      }}
    >
      {/* Content Container */}
      <Box
        sx={{
          position: 'relative',
          zIndex: 2,
          width: '100%',
          height: '100vh',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          padding: spacing.xl,
          overflow: 'hidden',
        }}
      >
        {/* Main Content Wrapper */}
        <Box
          sx={{
            maxWidth: '700px',
            width: '100%',
            display: 'flex',
            flexDirection: 'column',
            gap: spacing.lg,
            animation: 'fadeInUp 0.8s ease-out',
            '@keyframes fadeInUp': {
              from: {
                opacity: 0,
                transform: 'translateY(20px)',
              },
              to: {
                opacity: 1,
                transform: 'translateY(0)',
              },
            },
          }}
        >
          {/* Main Heading */}
          <PromotionalHeading
            title={HEADING_DATA.title}
            subtitle={HEADING_DATA.subtitle}
          />

          {/* Dashboard Preview */}
          <Box
            sx={{
              width: '100%',
              display: 'flex',
              flexDirection: 'column',
              gap: spacing.md,
            }}
          >
            {/* Metrics Cards */}
            <Box sx={{ display: 'flex', gap: spacing.md }}>
              <MetricCard
                label={METRICS_DATA[0].label}
                value={METRICS_DATA[0].value}
                change={METRICS_DATA[0].change}
                changeType={METRICS_DATA[0].changeType}
                animation="slideInLeft"
                animationDelay={0.2}
              />
              <MetricCard
                label={METRICS_DATA[1].label}
                value={METRICS_DATA[1].value}
                change={METRICS_DATA[1].change}
                changeType={METRICS_DATA[1].changeType}
                animation="slideInRight"
                animationDelay={0.3}
              />
            </Box>

            {/* Analytics Chart */}
            <AnalyticsChart
              barHeights={CHART_BAR_HEIGHTS}
              title="Query Analytics"
            />

            {/* Bottom Row - Top Services and Testimonial */}
            <Box sx={{ display: 'flex', gap: spacing.md }}>
              <TopServicesList services={SERVICES_DATA} />
              <TestimonialCard
                quote={randomQuote?.quote}
                author={randomQuote?.author}
              />
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default PromotionalSection;
